import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

function AdminContent() {
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formData, setFormData] = useState({
    program: '',
    sectionName: '',
    sectionOrder: 1,
    pageTitle: '',
    pageOrder: 1,
    content: '',
    contentType: 'text',
    videoUrl: '',
    codeExample: ''
  });

  useEffect(() => {
    fetchContent();
    fetchPrograms();
  }, []);

  useEffect(() => {
    if (selectedProgram) {
      fetchContentByProgram(selectedProgram);
    } else {
      fetchContent();
    }
  }, [selectedProgram]);

  const fetchContent = async () => {
    try {
      const res = await api.get('/admin/content');
      setContent(res.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchContentByProgram = async (programId) => {
    try {
      const res = await api.get(`/admin/content?programId=${programId}`);
      setContent(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const res = await api.get('/admin/programs');
      setPrograms(res.data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingContent) {
        await api.put(`/admin/content/${editingContent._id}`, formData);
      } else {
        await api.post('/admin/content', formData);
      }

      setShowModal(false);
      resetForm();
      fetchContent();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving content');
    }
  };

  const handleEdit = (item) => {
    setEditingContent(item);
    setFormData({
      program: item.program._id || item.program,
      sectionName: item.sectionName,
      sectionOrder: item.sectionOrder,
      pageTitle: item.pageTitle,
      pageOrder: item.pageOrder,
      content: item.content || '',
      contentType: item.contentType || 'text',
      videoUrl: item.videoUrl || '',
      codeExample: item.codeExample || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await api.delete(`/admin/content/${id}`);
      fetchContent();
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting content');
    }
  };

  const resetForm = () => {
    setEditingContent(null);
    setFormData({
      program: '',
      sectionName: '',
      sectionOrder: 1,
      pageTitle: '',
      pageOrder: 1,
      content: '',
      contentType: 'text',
      videoUrl: '',
      codeExample: ''
    });
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-3">
        <div className="col">
          <h2>Content Management</h2>
        </div>
        <div className="col-auto">
          <select 
            className="form-select me-2 d-inline-block w-auto"
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
          >
            <option value="">All Programs</option>
            {programs.map(p => (
              <option key={p._id} value={p._id}>{p.title}</option>
            ))}
          </select>
          <button 
            className="btn btn-primary"
            onClick={() => { resetForm(); setShowModal(true); }}
          >
            + Add Content
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center"><div className="spinner-border"></div></div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Program</th>
                <th>Section</th>
                <th>Page Title</th>
                <th>Page Order</th>
                <th>Type</th>
                <th>Preview</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {content.map((item) => (
                <tr key={item._id}>
                  <td>{item.program?.title}</td>
                  <td>
                    <span className="badge bg-secondary">
                      #{item.sectionOrder} - {item.sectionName}
                    </span>
                  </td>
                  <td><strong>{item.pageTitle}</strong></td>
                  <td>{item.pageOrder}</td>
                  <td><span className="badge bg-info">{item.contentType}</span></td>
                  <td>
                    <small className="text-muted">
                      {item.content?.substring(0, 50)}...
                    </small>
                  </td>
                  <td>
                    <button 
                      className="btn btn-sm btn-warning me-1"
                      onClick={() => handleEdit(item)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-xl" style={{maxWidth: '90%'}}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingContent ? 'Edit' : 'Add'} Content</h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Program *</label>
                      <select 
                        className="form-select"
                        value={formData.program}
                        onChange={(e) => setFormData({...formData, program: e.target.value})}
                        required
                      >
                        <option value="">Select Program</option>
                        {programs.map(p => (
                          <option key={p._id} value={p._id}>{p.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Content Type</label>
                      <select 
                        className="form-select"
                        value={formData.contentType}
                        onChange={(e) => setFormData({...formData, contentType: e.target.value})}
                      >
                        <option value="text">Text/HTML</option>
                        <option value="video">Video</option>
                        <option value="code">Code Example</option>
                      </select>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Section Name *</label>
                      <input 
                        type="text" 
                        className="form-control"
                        value={formData.sectionName}
                        onChange={(e) => setFormData({...formData, sectionName: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Section Order *</label>
                      <input 
                        type="number" 
                        className="form-control"
                        value={formData.sectionOrder}
                        onChange={(e) => setFormData({...formData, sectionOrder: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Page Order *</label>
                      <input 
                        type="number" 
                        className="form-control"
                        value={formData.pageOrder}
                        onChange={(e) => setFormData({...formData, pageOrder: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Page Title *</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={formData.pageTitle}
                      onChange={(e) => setFormData({...formData, pageTitle: e.target.value})}
                      required
                    />
                  </div>

                  {formData.contentType === 'video' && (
                    <div className="mb-3">
                      <label className="form-label">Video URL (YouTube, Vimeo, etc.)</label>
                      <input 
                        type="text" 
                        className="form-control"
                        value={formData.videoUrl}
                        onChange={(e) => setFormData({...formData, videoUrl: e.target.value})}
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">
                      Content (HTML/Markdown supported) *
                    </label>
                    <div className="alert alert-info small mb-2">
                      <strong>Formatting Tips:</strong>
                      <ul className="mb-0 mt-1">
                        <li>&lt;h1&gt;Heading&lt;/h1&gt; - Main heading</li>
                        <li>&lt;h2&gt;Subheading&lt;/h2&gt; - Subheading</li>
                        <li>&lt;p&gt;Paragraph&lt;/p&gt; - Paragraph</li>
                        <li>&lt;strong&gt;Bold&lt;/strong&gt; - Bold text</li>
                        <li>&lt;code&gt;code&lt;/code&gt; - Inline code</li>
                        <li>&lt;ul&gt;&lt;li&gt;Item&lt;/li&gt;&lt;/ul&gt; - Bullet list</li>
                        <li>&lt;pre&gt;&lt;code&gt;code block&lt;/code&gt;&lt;/pre&gt; - Code block</li>
                      </ul>
                    </div>
                    <textarea 
                      className="form-control font-monospace"
                      style={{minHeight: '300px'}}
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      placeholder="Enter HTML content here..."
                      required
                    ></textarea>
                  </div>

                  {formData.contentType === 'code' && (
                    <div className="mb-3">
                      <label className="form-label">Code Example (Optional)</label>
                      <textarea 
                        className="form-control font-monospace"
                        style={{minHeight: '150px'}}
                        value={formData.codeExample}
                        onChange={(e) => setFormData({...formData, codeExample: e.target.value})}
                        placeholder="Enter code example..."
                      ></textarea>
                    </div>
                  )}

                  {/* Preview */}
                  {formData.content && (
                    <div className="mb-3">
                      <label className="form-label">Preview</label>
                      <div 
                        className="border p-3 bg-light"
                        dangerouslySetInnerHTML={{__html: formData.content}}
                      />
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingContent ? 'Update' : 'Create'} Content
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminContent;
